import { createSelector, EntityState } from '@reduxjs/toolkit'
import { defaults } from 'lodash-es'
import { denormalize } from 'normalizr'

import { IFilterProfile, IFilterProfileLite } from '$/types'
import { RootState } from 'app/store'
import { filterProfileSchema } from 'app/entities/schema/filterProfileSchema'

import { filterProfilesAdapter } from './filterProfilesSlice'

const selectFilterProfiles = (
  state: RootState
): EntityState<IFilterProfileLite, string> => state.entities.filterProfiles

/**
 * Method to fill required properties of IFilterProfile object with empty
 * result for cases where API did not return full object..
 * @param profile
 */
const ensureRequiredFilterProfileFieldsExist = (
  profile: IFilterProfileLite
): void => {
  defaults(profile, {
    operators: [],
    states: [],
    regions: [],
    lines: []
  })
}

/**
 * Selector to retrieve list of denormalized filterProfiles
 */
const denormalizedSelectAll = createSelector(
  selectFilterProfiles,
  (state: RootState) => state.entities.lines,
  (filterProfiles, lines) => {
    const denormalizedFilterProfiles: IFilterProfile[] = denormalize(
      filterProfiles.ids,
      [filterProfileSchema],
      /**
       * NOTE: This function should not be used going forward
       * because the operators, states, and regions in the
       * entities stores is deprecated. Use RTKQ with the '/systemScopes'
       * endpoint instead to directly retrieve denormalized filter profiles.
       * It is used already once within features/app/layout/Navbar/Filters/FilterModal/index.tsx,
       * but it's not a problem since the results aren't populated in the store/DB.
       * See https://github.com/JANA-Technology/Lighthouse-Web/pull/361 for more info.
       */
      {
        filterProfiles: filterProfiles.entities,
        operators: [],
        states: [],
        regions: [],
        continuousRoutes: lines.entities
      }
    )

    denormalizedFilterProfiles.forEach(ensureRequiredFilterProfileFieldsExist)

    return denormalizedFilterProfiles
  }
)

const denormalizedSelectById = createSelector(
  selectFilterProfiles,
  (state: RootState) => state.entities.lines,
  (_states: RootState, profileId: string) => profileId,
  (filterProfiles, lines, profileId) => {
    const denormalizedFilterProfile: IFilterProfile | undefined = denormalize(
      profileId,
      filterProfileSchema,
      // IMPORTANT: see above comment
      {
        filterProfiles: filterProfiles.entities,
        operators: [],
        states: [],
        regions: [],
        continuousRoutes: lines.entities
      }
    )

    if (denormalizedFilterProfile != null) {
      ensureRequiredFilterProfileFieldsExist(denormalizedFilterProfile)
    }

    return denormalizedFilterProfile
  }
)

export const normalizedFilterProfilesSelectors =
  filterProfilesAdapter.getSelectors(selectFilterProfiles)

export const denormalizedFilterProfilesSelectors = {
  selectAll: denormalizedSelectAll,
  selectById: denormalizedSelectById
}
